/*
  Fast Simon-backed autocomplete search dropdown - fetches product and popular-search
  suggestions directly from Fast Simon's Serving API and renders them into the
  searchRECS module (search-recs-default-grid.liquid), replacing the default recs
  once the shopper starts typing.

  Depends on fast-simon-tracking.js.liquid (window.FastSimonTracking) being loaded
  first.
*/
(function () {
  const searchRecs = document.querySelector('.js-search-recs');
  const searchRecsInput = document.querySelector('.js-search-recs-input');
  const searchRecsDefaults = document.querySelector('.js-search-recs-defaults');
  const searchRecsResults = document.querySelector('.js-search-recs-results');
  const searchRecsResultsCards = searchRecsResults ? searchRecsResults.querySelector('.search-recs__cards') : null;
  const searchRecsShowAllLink = searchRecsResults ? searchRecsResults.querySelector('.js-search-recs-show-all') : null;
  const searchRecsSuggestions = document.querySelector('.js-search-recs-suggestions');
  const searchRecsSuggestionsList = searchRecsSuggestions
    ? searchRecsSuggestions.querySelector('.search-recs__suggestions')
    : null;
  const searchRecsSkeletonCard = document.querySelector('.js-search-recs-skeleton-card');
  const searchRecsSkeletonPill = document.querySelector('.js-search-recs-skeleton-pill');
  const SEARCH_RECS_SKELETON_CARD_COUNT = 6;
  const SEARCH_RECS_SKELETON_PILL_COUNT = 4;

  const FAST_SIMON_UUID = '8ee3c5e8-6d27-4e3c-bae7-19638a81e5ac';
  const FAST_SIMON_STORE_ID = '9418836';
  const FAST_SIMON_HOST = window.location.hostname;
  const FAST_SIMON_NO_RESULTS_TEXT = "Sorry, No results found for your search";
  const FAST_SIMON_SHOW_ALL_RESULTS_TEXT = "Show all results for";
  const SEARCH_RESULTS_URL = searchRecsInput ? searchRecsInput.closest('form')?.getAttribute('action') : null;

  let fastSimonCdnCacheKey = null;
  let searchRecsHasResults = false;

  async function getFastSimonCdnCacheKey() {
    if (fastSimonCdnCacheKey) return fastSimonCdnCacheKey;

    const params = new URLSearchParams({
      UUID: FAST_SIMON_UUID,
      store_id: FAST_SIMON_STORE_ID,
    });
    const response = await fetch(`https://api.fastsimon.com/load?${params}`);
    const data = await response.json();
    fastSimonCdnCacheKey = data.cdn_cache_key;
    return fastSimonCdnCacheKey;
  }

  async function fetchFastSimonAutocompleteProducts(term) {
    const cdnCacheKey = await getFastSimonCdnCacheKey();
    const params = new URLSearchParams({
      q: term,
      UUID: FAST_SIMON_UUID,
      store_id: FAST_SIMON_STORE_ID,
      cdn_cache_key: cdnCacheKey,
      with_categories: '1',
    });
    const response = await fetch(`https://api.fastsimon.com/ac?${params}`);
    return response.json();
  }

  // Popular search term suggestions - doesn't need UUID/store_id/cdn_cache_key,
  // just the search term and the customer-facing storefront domain.
  async function fetchFastSimonPopularSearches(term) {
    const params = new URLSearchParams({ q: term, l: FAST_SIMON_HOST });
    const response = await fetch(`https://suggest.fastsimon.com/?${params}`);
    return response.json();
  }

  function debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  }

  // Formats a Fast Simon item's price using its own reported currency,
  // falling back to the store's active currency if none is given.
  function formatFastSimonPrice(amount, currency) {
    const num = parseFloat(amount);
    if (isNaN(num)) return '';

    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency || (window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD'),
      // "$" rather than "A$" for AUD.
      currencyDisplay: 'narrowSymbol',
    }).format(num);
  }

  // Ported from FSHooks.liquid's render-autocomplete-product-item hook: the autocomplete
  // will sometimes get "Vent Mount Vent Mount" - this returns unique words, "Vent Mount".
  function removeDuplicateWords(term) {
    const words = new Set();

    return term
      .split(/\s+/)
      .filter((word) => {
        const key = word.toLowerCase();
        if (words.has(key)) return false;
        words.add(key);
        return true;
      })
      .join(' ')
      .replace('Mounts Mount', 'Mount');
  }

  // Splits a Fast Simon item's raw title (e.g. "MAG Cases - McLaren Edition - iPhone 17
  // Pro Max MAG - McLaren 2026") into a category (first segment) and a deduped display
  // title (remaining segments), matching what FSHooks.liquid did for the old widget.
  function splitFastSimonTitle(rawTitle) {
    const parts = rawTitle.trim().split(/\s* - \s*/);

    if (parts.length <= 1) {
      return { category: '', title: removeDuplicateWords(rawTitle) };
    }

    const [first, ...rest] = parts;
    return {
      category: removeDuplicateWords(first),
      title: removeDuplicateWords(rest.join(' - ')),
    };
  }

  // Builds a `.search-recs__card` node from a Fast Simon /ac item, matching the
  // markup produced by the search-recs-product-card snippet for default recs.
  function buildFastSimonProductCard(item, term) {
    const locale = window.Shopify && Shopify.locale !== 'en' ? '/' + Shopify.locale : '';
    const price = formatFastSimonPrice(item.p || item.p_min, item.c);
    const { category: categoryText, title: titleText } = splitFastSimonTitle(item.l);

    const card = document.createElement('a');
    card.className = 'search-recs__card';
    card.href = locale + item.u;
    card.setAttribute('aria-label', item.l);
    card.addEventListener('click', () => {
      window.FastSimonTracking?.trackProductSelected(term, item.id, item.s);
      sendABTasty('SearchProductClick');
    });

    const imageWrap = document.createElement('div');
    imageWrap.className = 'search-recs__image-wrap';

    const image = document.createElement('img');
    image.className = 'search-recs__image lazyload';
    image.loading = 'lazy';
    image.width = 60;
    image.height = 60;
    image.src = item.t;
    image.alt = item.l;
    imageWrap.appendChild(image);

    const content = document.createElement('div');
    content.className = 'search-recs__content';

    const title = document.createElement('h3');
    title.className = 'search-recs__title';
    title.textContent = titleText;

    const category = document.createElement('span');
    category.className = 'search-recs__category';
    category.textContent = categoryText;

    const priceElement = document.createElement('p');
    priceElement.className = 'search-recs__price';
    priceElement.textContent = price;

    content.appendChild(title);
    content.appendChild(category);
    content.appendChild(priceElement);

    card.appendChild(imageWrap);
    card.appendChild(content);

    return card;
  }

  // Builds a clickable suggested-search term that re-runs the search with that term.
  function buildFastSimonSuggestion(suggestedTerm, term) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'search-recs__suggestion';
    button.textContent = suggestedTerm;
    button.addEventListener('click', () => {
      window.FastSimonTracking?.trackPopularSearchSelected(term, suggestedTerm);
      sendABTasty('SearchAutocompleteSuggestionsClick');

      if (searchRecsInput) {
        searchRecsInput.value = suggestedTerm;
        searchRecsInput.focus();
      }
      showSearchRecsLoading();
      performFastSimonSearch(suggestedTerm);
    });
    return button;
  }

  function appendSkeletons(container, template, count) {
    if (!container || !template) return;

    container.innerHTML = '';
    for (let i = 0; i < count; i++) {
      container.appendChild(template.content.cloneNode(true));
    }
  }

  function clearSearchRecsLoading() {
    searchRecs?.removeAttribute('aria-busy');
    searchRecs?.classList.remove('search-recs--refreshing');
  }

  // Loading state while a search is in flight. The first search shows grey
  // placeholders where the suggested searches and results will land. Later
  // searches keep the previous results on screen, faded, rather than flashing
  // back to placeholders on every keystroke.
  function showSearchRecsLoading() {
    searchRecs?.setAttribute('aria-busy', 'true');

    if (searchRecsHasResults) {
      searchRecs?.classList.add('search-recs--refreshing');
      return;
    }

    appendSkeletons(searchRecsResultsCards, searchRecsSkeletonCard, SEARCH_RECS_SKELETON_CARD_COUNT);
    appendSkeletons(searchRecsSuggestionsList, searchRecsSkeletonPill, SEARCH_RECS_SKELETON_PILL_COUNT);
    searchRecsShowAllLink?.classList.add('d-none');
    searchRecsDefaults?.classList.add('d-none');
    searchRecsSuggestions?.classList.remove('d-none');
    searchRecsResults?.classList.remove('d-none');
  }

  function showSearchRecsDefaults() {
    clearSearchRecsLoading();
    searchRecsHasResults = false;
    searchRecsDefaults?.classList.remove('d-none');
    searchRecsResults?.classList.add('d-none');
    searchRecsSuggestions?.classList.add('d-none');
  }

  function showSearchRecsResults(items, popularSearches, term) {
    clearSearchRecsLoading();
    searchRecsHasResults = true;

    if (searchRecsResultsCards) {
      searchRecsResultsCards.innerHTML = '';

      if (items.length) {
        items.forEach((item) => searchRecsResultsCards.appendChild(buildFastSimonProductCard(item, term)));
      } else {
        const emptyMessage = document.createElement('p');
        emptyMessage.className = 'search-recs__empty';
        emptyMessage.textContent = FAST_SIMON_NO_RESULTS_TEXT;
        searchRecsResultsCards.appendChild(emptyMessage);
      }
    }

    if (searchRecsSuggestionsList) {
      searchRecsSuggestionsList.innerHTML = '';
      popularSearches.forEach((suggestion) =>
        searchRecsSuggestionsList.appendChild(buildFastSimonSuggestion(suggestion.label, term))
      );
    }
    searchRecsSuggestions?.classList.toggle('d-none', !popularSearches.length);

    if (searchRecsShowAllLink) {
      searchRecsShowAllLink.classList.toggle('d-none', !items.length);

      if (items.length) {
        searchRecsShowAllLink.href = SEARCH_RESULTS_URL ? `${SEARCH_RESULTS_URL}?q=${encodeURIComponent(term)}` : '#';
        searchRecsShowAllLink.textContent = `${FAST_SIMON_SHOW_ALL_RESULTS_TEXT} "${term}"`;
      }
    }

    searchRecsDefaults?.classList.add('d-none');
    searchRecsResults?.classList.remove('d-none');
  }

  async function performFastSimonSearch(term) {
    if (!term) {
      showSearchRecsDefaults();
      return;
    }

    try {
      const [{ items }, { items: popularSearches }] = await Promise.all([
        fetchFastSimonAutocompleteProducts(term),
        fetchFastSimonPopularSearches(term),
      ]);

      // Bail if the input has changed since this search started, so a slow
      // response for an earlier keystroke can't clobber more recent results.
      if (searchRecsInput.value.trim() !== term) return;

      showSearchRecsResults(items || [], popularSearches || [], term);
    } catch (err) {
      console.error('Fast Simon search failed', err);
      showSearchRecsDefaults();
    }
  }

  const handleSearchRecsInput = debounce(performFastSimonSearch, 300);

  if (searchRecsInput) {
    searchRecsInput.addEventListener('input', function (e) {
      const term = e.target.value.trim();

      if (term) {
        showSearchRecsLoading();
      } else {
        showSearchRecsDefaults();
      }
      handleSearchRecsInput(term);
    });
  }

  if (searchRecsShowAllLink) {
    searchRecsShowAllLink.addEventListener('click', function () {
      sendABTasty('SearchAutocompleteShowAllResults');
    });
  }

  const headerSearchInput = document.querySelector('.js-header-search-input');
  if (headerSearchInput) {
    headerSearchInput.addEventListener('input', function handler(e) {
      if (e.target.value.length) {
        // send an event as soon as a character is entered, then remove the event, the event should only fire once
        sendABTasty("SearchHeaderInput");
        e.target.removeEventListener('input', handler);
      }
    });
  }

  // Custom search input clear ("x") button, replacing the native
  // input[type="search"] one (hidden via fs-search.scss). Dispatches "input"
  // so the search dropdown reacts the same way as if the field had been
  // cleared by hand.
  document.querySelectorAll('.js-search-clear').forEach(function (clearButton) {
    clearButton.addEventListener('click', function () {
      const input = clearButton.closest('.fs-search__bar')?.querySelector('input[type="search"]');
      if (!input) return;

      input.value = '';
      input.focus();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });
})();
